import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from './lib/auth';
import { UploadQueueProvider } from './context/UploadQueueContext';
import { Sidebar } from './components/Sidebar';
import { Topbar } from './components/Topbar';
import { TrialBanner } from './components/TrialBanner';
import { InAppFoundingBanner } from './components/FoundingCounter';
import { api } from './lib/api';
import DashboardPage from './pages/DashboardPage';
import UploadPage from './pages/UploadPage';
import BillsPage from './pages/BillsPage';
import BillDetailPage from './pages/BillDetailPage';
import LibraryPage from './pages/LibraryPage';
import ItemsPage from './pages/ItemsPage';
import ItemDetailPage from './pages/ItemDetailPage';
import VendorsPage from './pages/VendorsPage';
import VendorDetailPage from './pages/VendorDetailPage';
import StaffPage from './pages/StaffPage';
import StaffPayslipPage from './pages/StaffPayslipPage';
import ExpensesPage from './pages/ExpensesPage';
import VouchersPage from './pages/VouchersPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import AlertsPage from './pages/AlertsPage';
import SettingsPage from './pages/SettingsPage';
import CAExportPage from './pages/CAExportPage';
import OwnerPage from './pages/OwnerPage';
import DailySheetPage from './pages/DailySheetPage';
import DailySheetHistoryPage from './pages/DailySheetHistoryPage';
import VijayRasPage from './pages/VijayRasPage';
import InvoicingDashboard from './pages/invoicing/InvoicingDashboard';
// Sprint 2 (2026-07): shared list component used for Quotes / CN / DN
import DocumentsListPage from './pages/invoicing/DocumentsListPage';
import CustomersListPage from './pages/invoicing/CustomersListPage';
import CustomerEditPage from './pages/invoicing/CustomerEditPage';
import CustomerDetailPage from './pages/invoicing/CustomerDetailPage';
import InvoicesListPage from './pages/invoicing/InvoicesListPage';
import InvoiceEditPage from './pages/invoicing/InvoiceEditPage';
import InvoiceDetailPage from './pages/invoicing/InvoiceDetailPage';
import InvoiceSettingsPage from './pages/invoicing/InvoiceSettingsPage';
import GSTFilingPage from './pages/invoicing/GSTFilingPage';
import { LoginPage, SetupPage, ForgotPasswordPage, ResetPasswordPage, ResetWorkspacePage } from './pages/AuthPages';
import AuthCallback from './pages/AuthCallback';
import { BusinessSetupDialog } from './components/BusinessSetupDialog';
import LandingPage from './pages/public/LandingPage';
import PricingPage from './pages/public/PricingPage';
import TermsPage from './pages/public/TermsPage';
import PrivacyPage from './pages/public/PrivacyPage';
import RefundPage from './pages/public/RefundPage';
import ContactPage from './pages/public/ContactPage';
import TrustPage from './pages/public/TrustPage';

/** Root route: shows the public landing page for unauthenticated visitors and
 *  redirects logged-in users to /dashboard. This lets us keep the marketing
 *  surface at `/` (best for SEO + user memory) while preserving the deep-link
 *  URLs (`/bills`, `/items`, …) that existing users have bookmarked. */
function RootGate() {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
                Loading…
            </div>
        );
    }
    if (user) return <Navigate to="/dashboard" replace />;
    return <LandingPage />;
}

function ProtectedShell() {
    const { user, loading, initialized } = useAuth();
    const location = useLocation();
    const [alertsCount, setAlertsCount] = useState(0);
    // Sprint C: read the alerts on/off toggle so we can hide the Sidebar &
    // Topbar entry-points when the user has disabled price-change alerts.
    const [alertsEnabled, setAlertsEnabled] = useState(true);
    // Sprint 2026-07: opt-in toggle for Daily Sheet + Vijay Ras. Default
    // false so allowlisted users don't see them until they turn the switch
    // on from Settings.
    const [scannerEnabled, setScannerEnabled] = useState(false);
    // Phase 16: workspace (business profile) — prompt user to set it up on
    // first login. Bill uploads compare the workspace PAN/GSTINs against
    // each bill's extracted GSTIN so mis-routed bills are flagged.
    const [workspace, setWorkspace] = useState(null);
    const [workspaceLoaded, setWorkspaceLoaded] = useState(false);
    const [showBusinessSetup, setShowBusinessSetup] = useState(false);
    const refreshAlerts = useCallback(async () => {
        try {
            const a = await api.listAlerts();
            setAlertsCount(Array.isArray(a) ? a.length : 0);
        } catch (e) {
            /* ignore */
        }
    }, []);
    const refreshSettings = useCallback(async () => {
        try {
            const s = await api.getSettings();
            setAlertsEnabled(s?.alerts_enabled !== false);
            setScannerEnabled(s?.scanner_enabled === true);
        } catch (e) {
            /* ignore — settings endpoint failure shouldn't block the app */
        }
    }, []);
    const refreshWorkspace = useCallback(async () => {
        try {
            const r = await api.getWorkspace();
            setWorkspace(r?.configured ? r.workspace : null);
            setWorkspaceLoaded(true);
            // Sprint C: don't nag on every page load. Auto-open the business
            // setup only on the FIRST visit after signup (tracked via
            // localStorage). After that, the dialog is opened explicitly:
            //   1) from Settings > Business profile (edit button), or
            //   2) when the user tries to upload a bill without a configured
            //      workspace (see UploadPage).
            if (!r?.configured) {
                const seen = (() => {
                    try { return localStorage.getItem('emb_setup_seen') === '1'; }
                    catch { return false; }
                })();
                if (!seen) {
                    setShowBusinessSetup(true);
                }
            } else if (r?.needs_mobile) {
                // Backfill: legacy accounts that predate the mandatory-mobile
                // rollout. Force-open the setup dialog so the user has to
                // supply a phone number before doing anything else. We do
                // NOT set `emb_setup_seen` here — that flag governs the
                // first-run experience, not this backfill.
                setShowBusinessSetup(true);
            } else {
                // Once configured, we never need to nag again.
                try { localStorage.setItem('emb_setup_seen', '1'); } catch { /* noop */ }
            }
        } catch (e) {
            /* ignore — workspace endpoint failure shouldn't block the app */
            setWorkspaceLoaded(true);
        }
    }, []);
    useEffect(() => {
        if (user) {
            refreshAlerts();
            refreshWorkspace();
            refreshSettings();
            const t = setInterval(refreshAlerts, 30000);
            return () => clearInterval(t);
        }
    }, [refreshAlerts, refreshWorkspace, refreshSettings, user]);

    // Sprint 2C: listen for 402 Payment Required responses (locked/past_due
    // tenants trying to use paid features) and route them to the Billing
    // section with a friendly toast. Debounced so a flurry of 402s from a
    // burst of parallel requests only produces one toast per minute.
    const nav = useNavigate();
    useEffect(() => {
        let lastToastAt = 0;
        const onBlocked = (e) => {
            const d = e.detail || {};
            const now = Date.now();
            if (now - lastToastAt > 60_000) {
                lastToastAt = now;
                toast.error(d.message || 'Your access is locked. Please subscribe to continue.', {
                    action: {
                        label: 'Subscribe',
                        onClick: () => nav('/settings#billing'),
                    },
                    duration: 8000,
                });
            }
            // If the user is trying to upload / export, take them to the
            // Billing screen so subscribing is a single click away.
            const url = d.sourceUrl || '';
            if (/bills\/upload|ca-export|reconcile/.test(url)) {
                nav('/settings#billing');
            }
        };
        window.addEventListener('billlens:entitlement-blocked', onBlocked);
        return () => window.removeEventListener('billlens:entitlement-blocked', onBlocked);
    }, [nav]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
                Loading…
            </div>
        );
    }
    if (!user) {
        return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
    }
    // Sprint C: gate the alerts entry-points on the master toggle.
    const alertsEntry = alertsEnabled ? alertsCount : null; // null → hide UI
    return (
        <div className="flex min-h-screen app-noise">
            <Sidebar alertsCount={alertsEntry} scannerEnabled={scannerEnabled} />
            <div className="flex-1 min-w-0 flex flex-col">
                <Topbar alertsCount={alertsEntry} scannerEnabled={scannerEnabled} />
                <InAppFoundingBanner />
                <TrialBanner />
                <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 max-w-[1240px] w-full mx-auto">
                    <Outlet context={{
                        refreshAlerts,
                        workspace,
                        workspaceLoaded,
                        refreshWorkspace,
                        openBusinessSetup: () => setShowBusinessSetup(true),
                        alertsEnabled,
                        refreshSettings,
                    }} />
                </main>
            </div>
            <BusinessSetupDialog
                open={showBusinessSetup}
                onOpenChange={(v) => {
                    setShowBusinessSetup(v);
                    if (!v) {
                        // User dismissed the dialog — don't auto-open again on
                        // subsequent navigations.
                        try { localStorage.setItem('emb_setup_seen', '1'); } catch { /* noop */ }
                    }
                }}
                initial={workspace}
                dismissible={true}
                onSaved={(w) => {
                    setWorkspace(w);
                    setShowBusinessSetup(false);
                    try { localStorage.setItem('emb_setup_seen', '1'); } catch { /* noop */ }
                }}
            />
        </div>
    );
}

export default function App() {
    return (
        <div className="App">
            <AuthProvider>
                <UploadQueueProvider>
                    <BrowserRouter>
                        <AppRouter />
                    </BrowserRouter>
                    <Toaster richColors position="top-right" />
                </UploadQueueProvider>
            </AuthProvider>
        </div>
    );
}

/**
 * AppRouter — intercepts the Google OAuth callback (session_id in the
 * URL fragment) BEFORE the normal Routes so we can exchange it for a JWT
 * without ProtectedShell trying to /auth/me first. Detection must be
 * synchronous (during render) — a useEffect would run after the first
 * ProtectedShell render and cause a false /login bounce.
 */
function AppRouter() {
    const location = useLocation();
    if (location.hash && location.hash.includes('session_id=')) {
        return <AuthCallback />;
    }
    return (
        <Routes>
                        {/* Public marketing surface */}
                        <Route path="/" element={<RootGate />} />
                        <Route path="/pricing" element={<PricingPage />} />
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/refund" element={<RefundPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/trust" element={<TrustPage />} />

                        {/* Auth pages */}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/setup" element={<SetupPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/reset-workspace" element={<ResetWorkspacePage />} />

                        {/* PWA share-target fallback — the service worker
                            normally intercepts POST /share-target and 303s
                            to /upload?share=<id>. If the user hits this URL
                            with a GET (or before the SW is installed), send
                            them to /upload; the shareTarget helper will
                            find no payload and show an empty upload queue. */}
                        <Route path="/share-target" element={<Navigate to="/upload" replace />} />

                        {/* Authenticated app — mounted under /dashboard so `/` stays public,
                            but ALSO under top-level paths for backward-compat bookmarks. */}
                        <Route path="/dashboard" element={<ProtectedShell />}>
                            <Route index element={<DashboardPage />} />
                        </Route>
                        <Route element={<ProtectedShell />}>
                            <Route path="upload" element={<UploadPage />} />
                            <Route path="bills" element={<BillsPage />} />
                            <Route path="bills/:id" element={<BillDetailPage />} />
                            <Route path="library" element={<LibraryPage />} />
                            <Route path="items" element={<ItemsPage />} />
                            <Route path="items/detail" element={<ItemDetailPage />} />
                            <Route path="vendors" element={<VendorsPage />} />
                            <Route path="vendors/:vendorKey" element={<VendorDetailPage />} />
                            <Route path="staff" element={<StaffPage />} />
                            <Route path="staff/:staffId/payslip" element={<StaffPayslipPage />} />
                            <Route
                                path="expenses"
                                element={
                                    <ErrorBoundary
                                        label="ExpensesPage"
                                        title="Expenses view hit a snag"
                                        description="Your expense data is safe. This is usually caused by a stale grouping preference — resetting the view fixes it."
                                        onReset={() => {
                                            try {
                                                localStorage.removeItem('emb.exp.groupMode');
                                                localStorage.removeItem('emb.exp.groupByVendor');
                                            } catch { /* noop */ }
                                        }}
                                    >
                                        <ExpensesPage />
                                    </ErrorBoundary>
                                }
                            />
                            <Route path="alerts" element={<AlertsPage />} />
                            <Route path="vouchers" element={<VouchersPage />} />
                            <Route path="ca-export" element={<CAExportPage />} />
                            <Route path="settings" element={<SettingsPage />} />
                            {/* Sprint 2026-01: Owner (super-admin) dashboard. Server-side gated to OWNER_EMAIL. */}
                            <Route path="owner" element={<OwnerPage />} />
                            {/* Shakewallah integration — owner-only pages
                                (server-side gated by OWNER_EMAIL). See
                                /app/backend/daily_sheet.py for context. */}
                            <Route path="daily-sheet" element={<DailySheetPage />} />
                            <Route path="daily-sheet/history" element={<DailySheetHistoryPage />} />
                            <Route path="vijay-ras" element={<VijayRasPage />} />
                            {/* Phase 19: Invoicing */}
                            <Route path="invoicing" element={<InvoicingDashboard />} />
                            <Route path="invoicing/customers" element={<CustomersListPage />} />
                            <Route path="invoicing/customers/new" element={<CustomerEditPage isNew={true} />} />
                            <Route path="invoicing/customers/:id" element={<CustomerDetailPage />} />
                            <Route path="invoicing/customers/:id/edit" element={<CustomerEditPage isNew={false} />} />
                            <Route path="invoicing/invoices" element={<InvoicesListPage />} />
                            <Route path="invoicing/invoices/new" element={<InvoiceEditPage isNew={true} />} />
                            <Route path="invoicing/invoices/:id" element={<InvoiceDetailPage />} />
                            <Route path="invoicing/invoices/:id/edit" element={<InvoiceEditPage isNew={false} />} />
                            {/* Sprint 2: Quotes / Credit Notes / Debit Notes. All share the
                                InvoiceDetailPage + InvoiceEditPage under the hood — only
                                the create route differs (via docType prop). */}
                            <Route path="invoicing/quotes" element={<DocumentsListPage docType="quote" />} />
                            <Route path="invoicing/quotes/new" element={<InvoiceEditPage isNew={true} docType="quote" />} />
                            <Route path="invoicing/quotes/:id" element={<InvoiceDetailPage />} />
                            <Route path="invoicing/quotes/:id/edit" element={<InvoiceEditPage isNew={false} docType="quote" />} />
                            <Route path="invoicing/credit-notes" element={<DocumentsListPage docType="credit_note" />} />
                            <Route path="invoicing/credit-notes/new" element={<InvoiceEditPage isNew={true} docType="credit_note" />} />
                            <Route path="invoicing/credit-notes/:id" element={<InvoiceDetailPage />} />
                            <Route path="invoicing/credit-notes/:id/edit" element={<InvoiceEditPage isNew={false} docType="credit_note" />} />
                            <Route path="invoicing/debit-notes" element={<DocumentsListPage docType="debit_note" />} />
                            <Route path="invoicing/debit-notes/new" element={<InvoiceEditPage isNew={true} docType="debit_note" />} />
                            <Route path="invoicing/debit-notes/:id" element={<InvoiceDetailPage />} />
                            <Route path="invoicing/debit-notes/:id/edit" element={<InvoiceEditPage isNew={false} docType="debit_note" />} />
                            {/* Sprint 2026-09 — new document types */}
                            <Route path="invoicing/delivery-challans" element={<DocumentsListPage docType="delivery_challan" />} />
                            <Route path="invoicing/delivery-challans/new" element={<InvoiceEditPage isNew={true} docType="delivery_challan" />} />
                            <Route path="invoicing/delivery-challans/:id" element={<InvoiceDetailPage />} />
                            <Route path="invoicing/delivery-challans/:id/edit" element={<InvoiceEditPage isNew={false} docType="delivery_challan" />} />
                            <Route path="invoicing/sales-orders" element={<DocumentsListPage docType="sales_order" />} />
                            <Route path="invoicing/sales-orders/new" element={<InvoiceEditPage isNew={true} docType="sales_order" />} />
                            <Route path="invoicing/sales-orders/:id" element={<InvoiceDetailPage />} />
                            <Route path="invoicing/sales-orders/:id/edit" element={<InvoiceEditPage isNew={false} docType="sales_order" />} />
                            <Route path="invoicing/purchase-orders" element={<DocumentsListPage docType="purchase_order" />} />
                            <Route path="invoicing/purchase-orders/new" element={<InvoiceEditPage isNew={true} docType="purchase_order" />} />
                            <Route path="invoicing/purchase-orders/:id" element={<InvoiceDetailPage />} />
                            <Route path="invoicing/purchase-orders/:id/edit" element={<InvoiceEditPage isNew={false} docType="purchase_order" />} />
                            <Route path="invoicing/gst-filing" element={<GSTFilingPage />} />
                            <Route path="invoicing/settings" element={<InvoiceSettingsPage />} />
                        </Route>
        </Routes>
    );
}
